/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import {
  Divider,
  FormControlOption,
  RadioButtons,
  SortableList,
  SortableListProps,
  SortableListStyle,
  TextField,
} from '@squiz/sds';
import { CDP_STRINGS } from 'apps/customer-data-platform/src/Constants/Strings.constants';
import property from 'lodash/property';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SEGMENTATION_DATA_IDS } from '../../../Segmentation/Segmentation.constants';
import { SegmentGroupSwitcherProps } from './SegmentGroupSwitcher.props';
import { SegmentGroupSwitcherOperator, SegmentPriorityItem } from './SegmentGroupSwitcher.types';

/**
 * Load the segment into the segment builder.
 */
export const SegmentGroupSwitcher = (props: SegmentGroupSwitcherProps): JSX.Element => {
  const {
    disabled,
    errors,
    groupName,
    multiple,
    onNameChange,
    onOperatorChange,
    onSwitch,
    operator,
    segmentPriority,
    onSegmentPriorityChange,
  } = props;
  const { t } = useTranslation();

  // Labels
  const labels = useMemo(() => {
    return {
      groupSwitcherLabelByHierarchy: t(CDP_STRINGS.groupSwitcherLabelByHierarchy),
      groupSwitcherLabelByRecency: t(CDP_STRINGS.groupSwitcherLabelByRecency),
      groupSwitcherMultipleDescription: t(CDP_STRINGS.groupSwitcherMultipleDescription),
      groupSwitcherMultipleLabel: t(CDP_STRINGS.groupSwitcherMultipleLabel),
      groupSwitcherPriorityDescription: t(CDP_STRINGS.groupSwitcherPriorityDescription),
      groupSwitcherRecencyDescription: t(CDP_STRINGS.groupSwitcherRecencyDescription),
      groupSwitcherSingleDescription: t(CDP_STRINGS.groupSwitcherSingleDescription),
      groupSwitcherSingleLabel: t(CDP_STRINGS.groupSwitcherSingleLabel),
      groupSwitcherTitle: t(CDP_STRINGS.groupSwitcherTitle),
    };
  }, [t]);

  const radioOptions: Array<FormControlOption> = [
    {
      heading: labels.groupSwitcherSingleLabel,
      key: `single`,
      label: labels.groupSwitcherSingleLabel,
      value: `single`,
    },
    {
      heading: labels.groupSwitcherMultipleLabel,
      key: `grouped`,
      label: labels.groupSwitcherMultipleLabel,
      value: `grouped`,
    },
  ];

  const radioRef = useRef<HTMLInputElement>(null);
  const groupNameRef = useRef<HTMLInputElement>(null);

  const sortableListProps: SortableListProps<SegmentPriorityItem> = {
    items: segmentPriority,
    onChange: onSegmentPriorityChange,
    presentationStyle: SortableListStyle.GreyBox,
    renderContent: (item) => {
      return <>{item.name}</>;
    },
  };

  return (
    <div className="segment-group-switcher sds-card-container">
      <div className="segment-group-switcher__radio-buttons sds-card">
        <RadioButtons
          fieldLabel="Segment type switcher"
          hideLabel
          id="group-switcher"
          name="group-switcher"
          options={radioOptions}
          radioCallback={(option): void => {
            onSwitch(option.value === 'grouped');
          }}
          readOnly={disabled}
          ref={radioRef}
          required
          value={multiple ? 'grouped' : 'single'}
        />
        <div className="segment-group-switcher__description">
          {multiple ? labels.groupSwitcherMultipleDescription : labels.groupSwitcherSingleDescription}
        </div>
        {multiple && (
          <>
            <Divider />
            <h3 className="segment-group-switcher__group-title sds-heading--level-3">{labels.groupSwitcherTitle}</h3>
            <div className="segment-group-switcher__group-name">
              <TextField
                errorMessage={property(`group-name`)(errors) as string}
                fieldLabel="Group name"
                id="segment-group-name"
                name="segment-group-name"
                onChange={(): void => {
                  onNameChange(groupNameRef.current?.value || ``);
                }}
                ref={groupNameRef}
                required
                value={groupName || ``}
              />
            </div>
            <div className="segment-group-switcher__group-priority" data-id="segment-group-switcher__group-priority">
              <RadioButtons
                fieldLabel="Segment priority"
                id="group-priority"
                name="group-priority"
                options={[
                  {
                    heading: labels.groupSwitcherLabelByRecency,
                    key: SegmentGroupSwitcherOperator.ByRecency,
                    label: labels.groupSwitcherLabelByRecency,
                    value: SegmentGroupSwitcherOperator.ByRecency,
                  },
                  {
                    heading: labels.groupSwitcherLabelByHierarchy,
                    key: SegmentGroupSwitcherOperator.ByHierarchy,
                    label: labels.groupSwitcherLabelByHierarchy,
                    value: SegmentGroupSwitcherOperator.ByHierarchy,
                  },
                ]}
                radioCallback={(option): void => {
                  onOperatorChange(option.value as SegmentGroupSwitcherOperator);
                }}
                required
                value={operator}
              />
              {operator === SegmentGroupSwitcherOperator.ByRecency && (
                <div className="segment-group-switcher__group-description">
                  {labels.groupSwitcherRecencyDescription}
                </div>
              )}
              {operator === SegmentGroupSwitcherOperator.ByHierarchy && (
                <>
                  <div className="segment-group-switcher__group-description">
                    {labels.groupSwitcherPriorityDescription}
                  </div>
                  <div
                    className="segment-group-switcher__group-priority-list"
                    data-id={SEGMENTATION_DATA_IDS.segmentGroupSwitcherPriorityList}
                  >
                    <SortableList {...sortableListProps} />
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
